import React from 'react'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcOverlaySlider,
  PixelPageContainer,
  Section,
  SectionContent,
} from 'pixel-base'
import {
  getLeadTimes,
  type LeadTimeItem,
  type LeadTimeRow,
  getProductBom,
} from 'utils/api-routes-planning'
import { UploadIcon } from 'utils/icons/upload-icon'

import LeadTimeTable from './components/LeadTimeTable'
import LeadTimeUpload from './components/LeadTimeUpload'

const mapLeadTimeItemsToRows = (items: LeadTimeItem[]): LeadTimeRow[] => {
  return items.map((item) => ({
    id: item.id,
    product: item.product,
    product_id: item.product.product_id,
    product_sku: item.product.product_sku || '',
    product_image_path: item.product.product_image_path || '',
    product_name: item.product.product_name || '',
    lead_time: item.product_lead_time || 0,
    product_buffer_stock: item.product_buffer_stock || 0,
    moq: item.product_moq || 0,
    new_lead_time: undefined,
    new_moq: undefined,
  }))
}

const breadcrumbs = [
  {
    name: 'Production Planning',
    route: '/planning',
  },
  {
    name: 'Lead Time & MOQ Overview',
    active: true,
  },
]

const LeadTime: React.FC = () => {
  const [showUploadSlider, setShowUploadSlider] = React.useState(false)
  const [rows, setRows] = React.useState<LeadTimeRow[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [productType, setProductType] = React.useState('FG')

  React.useEffect(() => {
    setIsLoading(true)
    const subscription = getLeadTimes({
      product_type: productType,
    }).subscribe({
      next: (res) => {
        const items = res.response?.data || []
        const mappedRows = mapLeadTimeItemsToRows(items)
        setRows(mappedRows)
        setIsLoading(false)
      },
      error: () => {
        setRows([])
        setIsLoading(false)
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [productType])

  const handleParsed = (parsed: LeadTimeRow[]) => {
    setRows(parsed || [])
  }

  const handleRowChange = (index: number, row: LeadTimeRow) => {
    setRows((prev) => {
      const copy = [...prev]
      copy[index] = row
      return copy
    })
  }

  const handleUploadSuccess = () => {
    // Close the slider
    setShowUploadSlider(false)
    // Refresh data after successful upload
    setIsLoading(true)
    getLeadTimes({
      product_type: productType,
    }).subscribe({
      next: (res) => {
        const items = res.response?.data || []
        const mappedRows = mapLeadTimeItemsToRows(items)
        setRows(mappedRows)
        setIsLoading(false)
      },
      error: () => {
        setIsLoading(false)
      },
    })
  }

  const loadBomData = (productId: number | string, rowIndex: number) => {
    setRows((prev) => {
      const updated = [...prev]
      updated[rowIndex] = {
        ...updated[rowIndex],
        isBomLoading: true,
        bomError: null,
      }
      return updated
    })

    getProductBom(productId).subscribe({
      next: (res) => {
        setRows((prev) => {
          const updated = [...prev]
          updated[rowIndex] = {
            ...updated[rowIndex],
            bomItems: res.response?.data || [],
            bomError: res.response?.error || null,
            isBomLoading: false,
          }
          return updated
        })
      },
      error: () => {
        setRows((prev) => {
          const updated = [...prev]
          updated[rowIndex] = {
            ...updated[rowIndex],
            bomItems: [],
            bomError: 'Failed to load product BOM data',
            isBomLoading: false,
          }
          return updated
        })
      },
    })
  }

  const handleRowClick = (_row: LeadTimeRow) => {
    // Row click handler - can be used for other purposes if needed
  }

  const handleRowExpandToggled = (row: LeadTimeRow, isExpanded: boolean) => {
    // Only allow expansion for Finished Goods
    if (productType !== 'FG') return

    if (!row.product_id) return

    const rowIndex = rows.findIndex((r) => r.id === row.id)
    if (rowIndex === -1) return

    setRows((prev) => {
      const updated = [...prev]
      updated[rowIndex] = {
        ...updated[rowIndex],
        isExpanded: isExpanded,
      }
      return updated
    })

    // Load BOM data when expanding
    if (isExpanded && (!row.bomItems || row.bomItems.length === 0)) {
      loadBomData(row.product_id, rowIndex)
    }
  }

  const handleBomRefresh = (productId: number | string) => {
    const rowIndex = rows.findIndex((r) => r.product_id === productId)
    if (rowIndex !== -1) {
      loadBomData(productId, rowIndex)
    }
  }

  const handleBomAddSuccess = () => {
    // Find the expanded row and refresh its BOM data
    const expandedRowIndex = rows.findIndex((r) => r.isExpanded)
    if (expandedRowIndex !== -1 && rows[expandedRowIndex].product_id) {
      loadBomData(rows[expandedRowIndex].product_id!, expandedRowIndex)
    }
  }

  return (
    <>
      <DcFlexBox
        height="60px"
        style={{
          fontSize: '16px',
          padding: '8px 0px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 20px"
          width="100%"
        >
          <DcBreadcrumbs
            className="breadcrumbs"
            fontSize="16px"
            data={breadcrumbs}
          />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer padding="0px" height="calc(100vh - 61px)">
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '5px 20px' }}>
            <DcFlexBox
              height="auto"
              width="100%"
              justifyContent="space-between"
              flexDirection="row-reverse"
            >
              <DcFlexBox height="auto" width="auto" gap="20px">
                <DcButton
                  variant="primary"
                  size="sm"
                  onClick={() => setShowUploadSlider(true)}
                  withIcon={true}
                >
                  <UploadIcon color="white" size={16} />
                  <span>Upload Lead Time</span>
                </DcButton>
              </DcFlexBox>
              <DcDiv width="300px">
                <DcDropDown
                  customLabel="Porduct Type"
                  value={productType}
                  dropdownSize="sm"
                  required
                  onChange={(e) => setProductType(e.target.value)}
                  options={[
                    { label: 'Finished Goods (FG)', value: 'FG' },
                    { label: 'Manifacturing Materials (MM)', value: 'MM' },
                    { label: 'Consumer packaging (CP)', value: 'CP' },
                  ]}
                />
              </DcDiv>
            </DcFlexBox>
          </SectionContent>
        </Section>
        &nbsp;
        <Section style={{ padding: '0px 20px 20px' }}>
          <SectionContent>
            <LeadTimeTable
              rows={rows}
              onChangeRow={handleRowChange}
              onUpdateSuccess={handleUploadSuccess}
              onRowClick={handleRowClick}
              onRowExpandToggled={handleRowExpandToggled}
              onBomRefresh={handleBomRefresh}
              onBomAddSuccess={handleBomAddSuccess}
              isLoading={isLoading}
              productType={productType}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>

      <DcOverlaySlider
        isOpen={showUploadSlider}
        onClose={() => setShowUploadSlider(false)}
        title="Upload Lead Time"
        width="40%"
        padding="0px"
      >
        <LeadTimeUpload
          onParsed={handleParsed}
          onUploadSuccess={handleUploadSuccess}
        />
      </DcOverlaySlider>
    </>
  )
}

export default LeadTime
